/**
 * MSA tab — available from the Proposal phase onward.
 *
 * The MSA is sent as a state-based *package*, resolved from the account's state
 * via DOCUSIGN_CLIENT_CONFIGS.<tenant>.msaPackages. Each state's package is
 * authored in DocuSign as a single template that bundles every document, so a
 * package is normally one envelope — but the config is still a list and the
 * send still fans out, so this tab renders N envelopes and only reports
 * "Signed" once every one of them completes.
 *
 * Two paths:
 *   - DocuSign package send: account.sendMsa (templates resolved by state).
 *   - Manual submit: paste a URL + optional note, call account.submitMsaManual.
 *
 * Package envelope status lives in the `account.msaEnvelopes` rows; manual
 * submission status lives in the `account.msa` JSON column.
 */

import React from 'react';
import {Send, FileSignature, CheckCircle2, Clock, XCircle, FileText} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@common-web/components/ui/alert-dialog';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {Textarea} from '@common-web/components/ui/textarea';
import {Badge} from '@common-web/components/ui/badge';
import {Card} from '@common-web/components/ui/card';
import {trpc} from '@common-web/lib/trpc';
import {toast} from '@common-web/hooks/use-toast';

interface MsaEnvelope {
  id: string;
  envelopeId: string;
  templateId: string;
  label: string;
  state: string | null;
  sortOrder: number;
  docuSignStatus: string | null;
  sentAt: string | Date | null;
  signedAt: string | Date | null;
  // Per-recipient progress for broker-signer tenants (routing order:
  // client → broker → owner). Always null for tenants whose envelopes
  // complete on the client's signature alone.
  clientSignedAt?: string | Date | null;
  brokerSignedAt?: string | Date | null;
  signedDocumentS3Key: string | null;
}

interface MsaTabProps {
  accountsId: string;
  /** Account/company name — named in the send-confirmation copy. */
  companyName: string;
  msa: unknown;
  msaEnvelopes: MsaEnvelope[];
  subStage: string;
  lifecyclePhase: string;
}

// MSA can be initiated from Onboarding onward. Proposals now live in the
// Prospect phase, which has no MSA step.
const MSA_SENDABLE_PHASES: ReadonlyArray<string> = ['ONBOARDING', 'IMPLEMENTATION', 'ACTIVE'];

interface MsaShape {
  manualUrl?: string;
  manualNote?: string;
  signedAt?: string;
  docuSignStatus?: string;
}

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline';

function envelopeStatus(env: MsaEnvelope): {label: string; variant: BadgeVariant} {
  const status = env.docuSignStatus;
  if (status === 'COMPLETED' || status === 'SIGNED' || env.signedAt) {
    return {label: 'Signed', variant: 'default'};
  }
  if (status === 'DECLINED') return {label: 'Declined', variant: 'destructive'};
  if (status === 'VOIDED') return {label: 'Voided', variant: 'destructive'};
  // Broker-signer tenants route client → broker → owner, so an in-flight
  // envelope can sit between signers; surface who's up next.
  if (env.brokerSignedAt)
    return {label: 'Broker signed · awaiting countersign', variant: 'secondary'};
  if (env.clientSignedAt) return {label: 'Client signed · awaiting broker', variant: 'secondary'};
  if (status === 'DELIVERED') return {label: 'Opened', variant: 'secondary'};
  return {label: 'Sent', variant: 'secondary'};
}

function isSigned(env: MsaEnvelope): boolean {
  return env.docuSignStatus === 'COMPLETED' || env.docuSignStatus === 'SIGNED' || !!env.signedAt;
}

function packageBadge(
  envelopes: MsaEnvelope[],
  manual: MsaShape,
  packageSize: number,
): {label: string; variant: BadgeVariant} {
  if (manual.signedAt || manual.docuSignStatus === 'MANUAL') {
    return {label: 'Manual', variant: 'default'};
  }
  if (envelopes.length === 0) return {label: 'Not sent', variant: 'outline'};
  const signed = envelopes.filter(isSigned).length;
  // "Signed" only once every envelope in the *configured* package is signed —
  // a partially sent package (mid-send failure) must not read as complete.
  if (signed === packageSize) return {label: 'Signed', variant: 'default'};
  // A bundled single-envelope package mirrors its envelope's own status
  // (Opened / Client signed / …) — a signed counter would just read
  // "Sent · 0/1 signed".
  if (packageSize === 1 && envelopes.length === 1) return envelopeStatus(envelopes[0]);
  if (packageSize === 1) return {label: 'Sent', variant: 'secondary'};
  return {label: `Sent · ${signed}/${packageSize} signed`, variant: 'secondary'};
}

export function MsaTab({
  accountsId,
  companyName,
  msa,
  msaEnvelopes,
  lifecyclePhase,
}: MsaTabProps): React.ReactElement {
  const utils = trpc.useUtils();
  const manual = (msa ?? {}) as MsaShape;
  const envelopes = [...(msaEnvelopes ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);

  const [manualUrl, setManualUrl] = React.useState('');
  const [manualNote, setManualNote] = React.useState('');
  const [showManualForm, setShowManualForm] = React.useState(false);
  const [confirmSendOpen, setConfirmSendOpen] = React.useState(false);

  const preview = trpc.account.previewMsaPackage.useQuery({accountsId});

  const invalidate = () => {
    utils.account.getById.invalidate({accountsId});
    utils.account.getActivity.invalidate({accountsId});
    utils.account.list.invalidate();
  };

  const sendMsa = trpc.account.sendMsa.useMutation({onSuccess: invalidate, onError: invalidate});

  const submitManual = trpc.account.submitMsaManual.useMutation({
    onSuccess: () => {
      invalidate();
      setManualUrl('');
      setManualNote('');
      setShowManualForm(false);
    },
  });

  const sentTemplateIds = new Set(envelopes.map((e) => e.templateId));
  const previewContracts = preview.data?.contracts ?? [];
  const remainingCount = previewContracts.filter((c) => !sentTemplateIds.has(c.templateId)).length;
  // Configured package size: envelopes already sent plus the contracts in the
  // current package that haven't been sent yet. While the preview is
  // loading/unavailable this degrades to envelopes.length (remainingCount 0).
  const packageSize = envelopes.length + remainingCount;
  const status = packageBadge(envelopes, manual, packageSize);
  const allSigned =
    envelopes.length > 0 && envelopes.length === packageSize && envelopes.every(isSigned);

  // Show the send UI while the account is in a sendable phase and the package
  // still has something left to send.
  const inSendablePhase = MSA_SENDABLE_PHASES.includes(lifecyclePhase);
  const canSendDocusign = inSendablePhase && remainingCount > 0 && !manual.signedAt;
  // Show the section (so preview loading + "not configured" reasons render) even
  // when there's nothing left to send; the actual send action stays gated on
  // `canSendDocusign` / the button's own disabled state.
  const showDocusignSection =
    inSendablePhase &&
    !manual.signedAt &&
    (canSendDocusign || preview.isLoading || preview.isError || !!preview.data?.reason);
  const canSubmitManual = inSendablePhase && !manual.signedAt && envelopes.length === 0;

  return (
    <Card className='space-y-4 border-border p-6 shadow-sm'>
      <div className='flex items-center justify-between'>
        <h3 className='text-sm font-semibold uppercase tracking-wide text-foreground'>
          MSA Package
        </h3>
        <Badge variant={status.variant}>{status.label}</Badge>
      </div>

      {/* Package contracts */}
      {envelopes.length > 0 ? (
        <div className='space-y-2 rounded-lg border border-border bg-muted/30 p-4'>
          <h4 className='text-sm font-semibold text-foreground'>Contracts</h4>
          <ul className='divide-y divide-border'>
            {envelopes.map((env) => {
              const s = envelopeStatus(env);
              const signed = isSigned(env);
              return (
                <li key={env.id} className='flex items-center justify-between gap-3 py-2'>
                  <div className='flex min-w-0 items-center gap-2'>
                    {signed ? (
                      <CheckCircle2 className='h-4 w-4 shrink-0 text-green-600' />
                    ) : env.docuSignStatus === 'DECLINED' || env.docuSignStatus === 'VOIDED' ? (
                      <XCircle className='h-4 w-4 shrink-0 text-red-600' />
                    ) : (
                      <Clock className='h-4 w-4 shrink-0 text-muted-foreground' />
                    )}
                    <span className='truncate text-sm text-foreground'>{env.label}</span>
                  </div>
                  <div className='flex items-center gap-2'>
                    {env.signedDocumentS3Key ? (
                      <DownloadSignedLink accountsId={accountsId} envelopeId={env.envelopeId} />
                    ) : null}
                    <Badge variant={s.variant}>{s.label}</Badge>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {/* Manual submission status */}
      {manual.manualUrl ? (
        <div className='space-y-1 rounded-lg border border-border bg-muted/30 p-4 text-sm'>
          <div className='flex items-center justify-between'>
            <h4 className='font-semibold text-foreground'>Manual submission</h4>
            <Badge variant='default'>Signed</Badge>
          </div>
          <a
            href={manual.manualUrl}
            target='_blank'
            rel='noreferrer'
            className='break-all text-primary hover:underline'
          >
            {manual.manualUrl}
          </a>
          {manual.manualNote ? <p className='text-muted-foreground'>{manual.manualNote}</p> : null}
        </div>
      ) : null}

      {allSigned ? (
        <p className='text-sm text-green-700'>This MSA package is fully signed.</p>
      ) : null}

      {/* Send via DocuSign */}
      {showDocusignSection ? (
        <div className='space-y-3 rounded-lg border border-border p-4'>
          <div className='flex items-center gap-2'>
            <Send className='h-4 w-4 text-muted-foreground' />
            <h4 className='text-sm font-semibold text-foreground'>Send via DocuSign</h4>
          </div>

          {preview.isLoading ? (
            <p className='text-sm text-muted-foreground'>Loading package…</p>
          ) : preview.isError ? (
            <p className='text-sm text-red-600'>
              {preview.error?.message ?? 'Could not load the MSA package. Please try again.'}
            </p>
          ) : preview.data && preview.data.reason ? (
            <p className='text-sm text-red-600'>{preview.data.reason}</p>
          ) : previewContracts.length > 0 ? (
            <>
              <p className='text-xs text-muted-foreground'>
                {preview.data?.state ? `Package for ${preview.data.state}` : 'Default package'} —
                sent as {remainingCount} DocuSign envelope{remainingCount === 1 ? '' : 's'}:
              </p>
              <ul className='space-y-1'>
                {previewContracts.map((c) => {
                  const alreadySent = sentTemplateIds.has(c.templateId);
                  return (
                    <li
                      key={c.templateId}
                      className='flex items-center gap-2 text-sm text-foreground'
                    >
                      <FileText className='h-4 w-4 text-muted-foreground' />
                      <span className={alreadySent ? 'text-muted-foreground line-through' : ''}>
                        {c.label}
                      </span>
                      {alreadySent ? (
                        <span className='text-xs text-muted-foreground'>(sent)</span>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </>
          ) : null}

          <div className='flex justify-end'>
            <Button
              onClick={() => setConfirmSendOpen(true)}
              disabled={
                remainingCount === 0 ||
                sendMsa.isPending ||
                preview.isError ||
                !!preview.data?.reason
              }
            >
              {sendMsa.isPending ? 'Sending…' : 'Send MSA package'}
            </Button>
          </div>
          {sendMsa.error ? (
            <div className='text-sm text-red-600'>{sendMsa.error.message}</div>
          ) : null}
        </div>
      ) : null}

      {/* Confirmation gate — the DocuSign send dispatches only on explicit confirm. */}
      <AlertDialog open={confirmSendOpen} onOpenChange={setConfirmSendOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send the MSA to {companyName}?</AlertDialogTitle>
            <AlertDialogDescription>
              This emails the MSA package to {companyName} via DocuSign for signature. Once sent, it
              cannot be unsent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                // The preview can refetch while the dialog is open; re-check
                // the send preconditions so a stale confirm can't dispatch.
                if (remainingCount > 0 && !preview.data?.reason) {
                  sendMsa.mutate({accountsId});
                }
                setConfirmSendOpen(false);
              }}
            >
              Send MSA package
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Manual submission */}
      {canSubmitManual ? (
        <div className='space-y-3 rounded-lg border border-border p-4'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              <FileSignature className='h-4 w-4 text-muted-foreground' />
              <h4 className='text-sm font-semibold text-foreground'>Manual MSA submission</h4>
            </div>
            {!showManualForm ? (
              <Button variant='outline' size='sm' onClick={() => setShowManualForm(true)}>
                Submit manually
              </Button>
            ) : null}
          </div>
          {showManualForm ? (
            <>
              <div className='space-y-1.5'>
                <Label htmlFor='msa-manual-url'>Signed document URL *</Label>
                <Input
                  id='msa-manual-url'
                  type='url'
                  value={manualUrl}
                  onChange={(e) => setManualUrl(e.target.value)}
                  placeholder='https://…'
                  required
                />
              </div>
              <div className='space-y-1.5'>
                <Label htmlFor='msa-manual-note'>Note (optional)</Label>
                <Textarea
                  id='msa-manual-note'
                  value={manualNote}
                  onChange={(e) => setManualNote(e.target.value)}
                  rows={2}
                />
              </div>
              <div className='flex justify-end gap-2'>
                <Button variant='outline' onClick={() => setShowManualForm(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() =>
                    submitManual.mutate({
                      accountsId,
                      manualUrl,
                      note: manualNote || undefined,
                    })
                  }
                  disabled={!manualUrl || submitManual.isPending}
                >
                  {submitManual.isPending ? 'Submitting…' : 'Submit'}
                </Button>
              </div>
              {submitManual.error ? (
                <div className='text-sm text-red-600'>{submitManual.error.message}</div>
              ) : null}
            </>
          ) : (
            <p className='text-xs text-muted-foreground'>
              Use manual submission if the MSA was signed outside of DocuSign.
            </p>
          )}
        </div>
      ) : null}
    </Card>
  );
}

function DownloadSignedLink({
  accountsId,
  envelopeId,
}: {
  accountsId: string;
  envelopeId: string;
}): React.ReactElement {
  const download = trpc.account.getMsaEnvelopeSignedUrl.useMutation();
  return (
    <Button
      variant='ghost'
      size='sm'
      className='h-7 px-2 text-xs'
      disabled={download.isPending}
      onClick={async () => {
        try {
          const url = await download.mutateAsync({accountsId, envelopeId});
          if (url) window.open(url, '_blank', 'noopener,noreferrer');
        } catch (err) {
          toast({
            variant: 'destructive',
            title: 'Could not open signed document',
            description: err instanceof Error ? err.message : 'Please try again.',
          });
        }
      }}
    >
      {download.isPending ? 'Opening…' : 'View signed'}
    </Button>
  );
}
